import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { DashboardData } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatEur } from '../lib/format';
import { Button, Card, Chip, Empty, Loading, PageHeader, StatCard } from '../components/ui';

export function Dashboard() {
  const { user, company } = useAuth();
  const { t, s } = useI18n();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['dashboard', company?.id], queryFn: () => capi<DashboardData>('/dashboard') });
  const d = q.data;
  return (
    <>
      <PageHeader crumb={t('dashCrumb')} title={t('dashTitle', { name: (user?.name ?? '').split(' ')[0] ?? '' })} sub={t('dashSub')}
        actions={<><Button icon="fas fa-download" onClick={() => window.open(`/api/companies/${company?.id}/invoices?limit=200`, '_blank')}>{t('btnExport')}</Button><Button variant="primary" icon="fas fa-plus" onClick={() => navigate('/invoices/new')}>{t('dashAction')}</Button></>} />
      {!d ? <Loading /> : (
        <div className="stack">
          <div className="grid kpis">
            <StatCard label={t('outstanding')} value={formatEur(d.outstandingCents, { decimals: false })} caption={t('openInvoicesN', { n: d.openInvoices })} icon="fas fa-file-invoice" />
            <StatCard label={t('overdue')} value={formatEur(d.overdueCents, { decimals: false })} caption={t('inDunningN', { n: d.inDunning })} icon="fas fa-clock" tone="danger" />
            <StatCard label={t('paidQuarter')} value={formatEur(d.paidQuarterCents, { decimals: false })} caption={`${d.quarterLabel} · ${t('inclIcp')}`} icon="fas fa-circle-check" tone="success" />
            <StatCard label={t('openTickets')} value={String(d.openTickets)} caption={t('breachingN', { n: d.ticketsBreachingToday })} icon="fas fa-headset" tone="accent" />
          </div>
          <div className="grid two">
            <Card pad={false} className="table">
              <div className="cardhead"><h2>{t('hNeedsAttention')}</h2><button type="button" className="linkbtn" onClick={() => navigate('/invoices')}>{t('allInvoices')}</button></div>
              <table>
                <thead><tr><th>{t('colInvoice')}</th><th>{t('colCustomer')}</th><th>{t('colStage')}</th><th className="r">{t('colAmount')}</th></tr></thead>
                <tbody>
                  {d.attention.length === 0 && <Empty />}
                  {d.attention.map((a) => (
                    <tr key={a.id} className="click" onClick={() => navigate(`/invoices/${a.id}`)}>
                      <td className="ref">{a.number}</td><td>{a.customerName}</td><td><Chip tone={a.tone}>{s(a.stage)}</Chip></td><td className="amount">{formatEur(a.totalCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <div className="stack">
              <Card>
                <h2>{t('hMailQueue')}</h2>
                <div className="stack" style={{ gap: '0.6rem' }}>
                  {d.queue.length === 0 && <div className="muted small">{t('nothingHere')}</div>}
                  {d.queue.map((m, i) => (
                    <div key={i} className="subcard row" style={{ flexWrap: 'nowrap', gap: '0.7rem' }}>
                      <i className={m.icon} aria-hidden="true" style={{ color: 'var(--accent)', fontSize: '0.9rem' }} />
                      <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{s(m.title)}</div><div className="small muted">{m.meta}</div></div>
                      <span className="mono small secondary">{m.when}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <h2 style={{ marginBottom: '0.5rem' }}>{t('hVatIcp')}</h2>
                <p className="secondary" style={{ margin: '0 0 0.75rem', fontSize: '0.8rem' }}>{t('filingCloses', { quarter: d.quarterLabel, date: d.vat.filingCloses })}</p>
                <div className="stack sm">
                  {[[t('vat21charged'), formatEur(d.vat.charged21Cents, { decimals: false })], [t('reverseCharge'), formatEur(d.vat.reverseChargeCents, { decimals: false })], [t('icpRows'), String(d.vat.icpRows)]].map(([k, v]) => (
                    <div key={k} className="row" style={{ justifyContent: 'space-between', fontSize: '0.82rem' }}><span className="secondary">{k}</span><span className="mono" style={{ fontWeight: 700 }}>{v}</span></div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
